import React, { useMemo, useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { format, parseISO, subMonths } from 'date-fns';
import { Colors } from 'library';
import { Card, Divider, Row, Select, Stack, Text } from 'library/ui';
import { useClient } from 'shared';

import {
    AllowanceBar,
    RemainingLabel,
    UsageBreakdown,
    type FeeFreeShiftAllowance,
} from 'parent-portal/shared/FeeFreeShifts/allowance';

const MONTHS_BACK = 6;

function monthOptions() {
    const today = new Date();
    return Array.from({ length: MONTHS_BACK }, (_, index) => {
        const month = subMonths(today, index);
        const value = format(month, 'yyyy-MM');
        return { key: value, value, label: format(month, 'MMMM yyyy') };
    });
}

export default function FeeFreeShiftsSection() {
    const options = useMemo(monthOptions, []);
    const [month, setMonth] = useState(options[0].value);
    const { data } = useClient<FeeFreeShiftAllowance[]>({
        url: `api/center-sub-pool/fee-free-shifts/?month=${month}`,
    });

    const allowances = data ?? [];
    if (allowances.length === 0) {
        return null;
    }

    const { limit, resets_on: resetsOn } = allowances[0];

    return (
        <Card padding="md">
            <Stack gap="md">
                <Row justify="between" align="center" gap="md" wrap>
                    <Text size="lg" weight="bold">
                        Fee-Free Shifts
                    </Text>
                    <Row gap="md" align="center">
                        <Select
                            value={month}
                            onChange={setMonth}
                            options={options}
                            tone="muted"
                            startAdornment={<CalendarTodayIcon sx={{ fontSize: 16, color: Colors.textSecondary }} />}
                        />
                        <Text size="sm" tone="secondary">
                            Resets {format(parseISO(resetsOn), 'MMM d')}
                        </Text>
                    </Row>
                </Row>
                <Text size="sm" tone="secondary">
                    Your ELC pool covers Tandem&apos;s administration fee on up to {limit} shifts per location each
                    month.
                </Text>
                {allowances.map((allowance) => (
                    <Stack key={allowance.business_location_id} gap="sm">
                        <Divider />
                        <Row justify="between" align="center" gap="md" wrap>
                            <Stack gap="none">
                                <Text weight="bold">{allowance.business_location_name}</Text>
                                <UsageBreakdown allowance={allowance} />
                            </Stack>
                            <AllowanceBar allowance={allowance} />
                            <RemainingLabel allowance={allowance} />
                        </Row>
                    </Stack>
                ))}
            </Stack>
        </Card>
    );
}
